// src/components/GameModeSelector.tsx
import React from "react";

type GameMode = "local" | "vsAI" | "online";

interface GameModeSelectorProps {
  gameMode: GameMode;
  connected: boolean;
  roomCode: string | null;
  opponentUsername?: string | null;
  onModeChange: (mode: GameMode) => void;
  onShowLobby: () => void;
  onShowToast?: (message: string) => void;
}

export const GameModeSelector: React.FC<GameModeSelectorProps> = ({
  gameMode,
  connected,
  roomCode,
  opponentUsername,
  onModeChange,
  onShowLobby,
  onShowToast,
}) => {
  return (
    <div className="mode-selector-bar" style={{ width: "100%" }}>
      <div
        style={{
          fontWeight: "bold",
          marginBottom: "5px",
          color: "#333",
          fontSize: "14px",
        }}
      >
        Game Mode:
      </div>

      {/* Mode Selection Radio Buttons */}
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: "5px",
          marginBottom: "5px",
        }}
      >
        {/* Local Mode */}
        <label
          style={{
            display: "flex",
            alignItems: "center",
            gap: "5px",
            cursor: "pointer",
            padding: "5px",
            backgroundColor: gameMode === "local" ? "#e3f2fd" : "#f5f5f5",
            border: `2px solid ${gameMode === "local" ? "#2196F3" : "#ddd"}`,
            borderRadius: "6px",
            transition: "all 0.2s",
          }}
        >
          <input
            type="radio"
            name="gameMode"
            value="local"
            checked={gameMode === "local"}
            onChange={() => onModeChange("local")}
            style={{
              width: "18px",
              height: "18px",
              cursor: "pointer",
            }}
          />
          <span
            style={{
              fontSize: "14px",
              fontWeight: gameMode === "local" ? "bold" : "normal",
              color: gameMode === "local" ? "#1976d2" : "#333",
            }}
          >
            🎮 Play Local (Player vs Player)
          </span>
        </label>

        {/* AI Mode */}
        <label
          style={{
            display: "flex",
            alignItems: "center",
            gap: "5px",
            cursor: "pointer",
            padding: "5px",
            backgroundColor: gameMode === "vsAI" ? "#e3f2fd" : "#f5f5f5",
            border: `2px solid ${gameMode === "vsAI" ? "#2196F3" : "#ddd"}`,
            borderRadius: "6px",
            transition: "all 0.2s",
          }}
        >
          <input
            type="radio"
            name="gameMode"
            value="vsAI"
            checked={gameMode === "vsAI"}
            onChange={() => onModeChange("vsAI")}
            style={{
              width: "18px",
              height: "18px",
              cursor: "pointer",
            }}
          />
          <span
            style={{
              fontSize: "14px",
              fontWeight: gameMode === "vsAI" ? "bold" : "normal",
              color: gameMode === "vsAI" ? "#1976d2" : "#333",
            }}
          >
            🤖 Play vs Computer (AI)
          </span>
        </label>

        {/* Online Mode */}
        <label
          style={{
            display: "flex",
            alignItems: "center",
            gap: "5px",
            cursor: "pointer",
            padding: "5px",
            backgroundColor: gameMode === "online" ? "#e3f2fd" : "#f5f5f5",
            border: `2px solid ${gameMode === "online" ? "#2196F3" : "#ddd"}`,
            borderRadius: "6px",
            transition: "all 0.2s",
          }}
        >
          <input
            type="radio"
            name="gameMode"
            value="online"
            checked={gameMode === "online"}
            onChange={() => onModeChange("online")}
            style={{
              width: "18px",
              height: "18px",
              cursor: "pointer",
            }}
          />
          <span
            style={{
              fontSize: "14px",
              fontWeight: gameMode === "online" ? "bold" : "normal",
              color: gameMode === "online" ? "#1976d2" : "#333",
            }}
          >
            🌐 Play Online (Multiplayer)
          </span>
        </label>
      </div>

      {/* Online Mode Buttons */}
      {gameMode === "online" && (
        <>
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "5px",
              marginTop: "10px",
              marginBottom: "10px",
            }}
          >
            <button
              onClick={() => connected && onShowLobby()}
              disabled={!connected}
              style={{
                padding: "4px 6px",
                backgroundColor: connected ? "#2196F3" : "#ccc",
                color: "white",
                border: "none",
                borderRadius: "8px",
                cursor: connected ? "pointer" : "not-allowed",
                fontSize: "13px",
                fontWeight: "600",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "5px",
                transition: "all 0.2s",
              }}
              onMouseEnter={(e) => {
                if (connected) {
                  e.currentTarget.style.backgroundColor = "#1976D2";
                  e.currentTarget.style.transform = "translateY(-2px)";
                  e.currentTarget.style.boxShadow = "0 4px 8px rgba(0,0,0,0.2)";
                }
              }}
              onMouseLeave={(e) => {
                if (connected) {
                  e.currentTarget.style.backgroundColor = "#2196F3";
                  e.currentTarget.style.transform = "translateY(0)";
                  e.currentTarget.style.boxShadow = "none";
                }
              }}
            >
              <span style={{ fontSize: "16px" }}>🔍</span>
              Join Existing Game (Guest)
            </button>

            <button
              onClick={() => connected && onShowLobby()}
              disabled={!connected}
              style={{
                padding: "4px 6px",
                backgroundColor: connected ? "#2196F3" : "#ccc",
                color: "white",
                border: "none",
                borderRadius: "8px",
                cursor: connected ? "pointer" : "not-allowed",
                fontSize: "13px",
                fontWeight: "600",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "5px",
                transition: "all 0.2s",
              }}
              onMouseEnter={(e) => {
                if (connected) {
                  e.currentTarget.style.backgroundColor = "#1976D2";
                  e.currentTarget.style.transform = "translateY(-2px)";
                  e.currentTarget.style.boxShadow = "0 4px 8px rgba(0,0,0,0.2)";
                }
              }}
              onMouseLeave={(e) => {
                if (connected) {
                  e.currentTarget.style.backgroundColor = "#2196F3";
                  e.currentTarget.style.transform = "translateY(0)";
                  e.currentTarget.style.boxShadow = "none";
                }
              }}
            >
              <span style={{ fontSize: "16px" }}>🎮</span>
              Wait for Opponent in Lobby (Host)
            </button>

            <button
              onClick={() => connected && onShowLobby()}
              disabled={!connected}
              style={{
                padding: "4px 6px",
                backgroundColor: connected ? "#ff9800" : "#ccc",
                color: "white",
                border: "none",
                borderRadius: "8px",
                cursor: connected ? "pointer" : "not-allowed",
                fontSize: "13px",
                fontWeight: "600",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "5px",
                transition: "all 0.2s",
              }}
              onMouseEnter={(e) => {
                if (connected) {
                  e.currentTarget.style.backgroundColor = "#f57c00";
                  e.currentTarget.style.transform = "translateY(-2px)";
                  e.currentTarget.style.boxShadow = "0 4px 8px rgba(0,0,0,0.2)";
                }
              }}
              onMouseLeave={(e) => {
                if (connected) {
                  e.currentTarget.style.backgroundColor = "#ff9800";
                  e.currentTarget.style.transform = "translateY(0)";
                  e.currentTarget.style.boxShadow = "none";
                }
              }}
            >
              <span style={{ fontSize: "16px" }}>➕</span>
              Create New Game
            </button>
          </div>

          {/* Connection Status */}
          <div className="connection-status-inline">
            <span className={connected ? "connected" : "disconnected"}>
              {connected ? "✅ Connected" : "❌ Disconnected"}
            </span>
            {roomCode && (
              <span className="room-code-inline">
                Room: <strong>{roomCode}</strong>
                {opponentUsername && (
                  <span style={{ marginLeft: "8px" }}>
                    vs <strong style={{ color: "#1976d2" }}>{opponentUsername}</strong>
                  </span>
                )}
              </span>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default GameModeSelector;
